import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Form } from 'react-bootstrap';
import {
	BsGripVertical,
	BsPlusLg,
	BsCheckLg,
	BsXLg,
} from 'react-icons/bs';

import TruncatedDescription from '../../../../BayDashboard/components/TruncatedDescription';
import ActionButton from '../../../../BayDropdown/ActionButton';
import BaySwitch from '../../../../BaySwitch';
import BayFieldWithSpinner from '../../../../TopNav/components/BayFieldSpinner';
import { getValidUrl } from '../../../../utilities/otherFuncs';
import { urlRegex } from '../../../../utilities/regex';

import '../../EditWidget/EditWidget.scss';

import type { TranslationFunction } from '../../../../types/common';

interface Link {
	id: string;
	label: string;
	path: string;
	isVisible: boolean;
}

interface Section {
	sectionTitle?: string;
	showSection?: boolean;
	options?: Link[];
}

interface Sections {
	firstTabLinks?: Section;
	secondTabLinks?: Section;
	thirdTabLinks?: Section;
}

interface WidgetCustomizationData {
	sections?: Sections;
}

interface DefaultWidgetStyles {
	sections?: Sections;
}

interface LaunchpadSettingsData {
	widgets?: {
		[widgetId: string]: {
			sections?: Sections;
		};
	};
}

interface EditModalInputFields {
	[widgetId: string]: {
		sections?: Sections;
	};
}

interface WidgetFinishStatus {
	[widgetId: string]: boolean;
}

interface UrlValidationState {
	[linkId: string]: {
		isValid: boolean;
		message: string;
	};
}

interface UrlLoadingState {
	[linkId: string]: boolean;
}

interface ShowSection {
	firstTabLinks: boolean | null;
	thirdTabLinks: boolean | null;
}

interface EditOtherlinksProps {
	t: TranslationFunction;
	editModalInputFields: EditModalInputFields;
	setEditModalInputFields: React.Dispatch<React.SetStateAction<EditModalInputFields>>;
	widgetId: string;
	widgetFinishStatus: WidgetFinishStatus;
	widgetCustomizationData?: WidgetCustomizationData;
	editLinkName: string;
	setEditLinkName: React.Dispatch<React.SetStateAction<string>>;
	launchpadSettingsData?: LaunchpadSettingsData;
	urlValidationState: UrlValidationState;
	urlLoadingState: UrlLoadingState;
	setUrlValidationState: React.Dispatch<React.SetStateAction<UrlValidationState>>;
	setUrlLoadingState: React.Dispatch<React.SetStateAction<UrlLoadingState>>;
	defaultWidgetStyles?: DefaultWidgetStyles;
}

type TabKey = 'firstTabLinks' | 'secondTabLinks' | 'thirdTabLinks';

const EditOtherlinks: React.FC<EditOtherlinksProps> = ({
	t,
	editModalInputFields,
	setEditModalInputFields,
	widgetId,
	widgetFinishStatus,
	widgetCustomizationData,
	editLinkName,
	setEditLinkName,
	launchpadSettingsData,
	urlValidationState,
	urlLoadingState,
	setUrlValidationState,
	setUrlLoadingState,
	defaultWidgetStyles
}) => {
	const [currentTab, setCurrentTab] = useState<TabKey>('firstTabLinks');
	const [showSection, setShowSection] = useState<ShowSection>({
		firstTabLinks: null,
		thirdTabLinks: null,
	});
	const [editingLinkId, setEditingLinkId] = useState<string | null>(null);

	useEffect(() => {
		setShowSection({
			firstTabLinks:
				widgetFinishStatus[widgetId] &&
				widgetCustomizationData?.sections?.firstTabLinks
					? widgetCustomizationData?.sections?.firstTabLinks?.showSection ?? false
					: defaultWidgetStyles?.sections?.firstTabLinks?.showSection || false,
			thirdTabLinks:
				widgetFinishStatus[widgetId] &&
				widgetCustomizationData?.sections?.thirdTabLinks
					? widgetCustomizationData?.sections?.thirdTabLinks?.showSection ?? false
					: defaultWidgetStyles?.sections?.thirdTabLinks?.showSection || false,
		});
	}, []);

	useEffect(() => {
		const currentLinks =
			editModalInputFields?.[widgetId]?.sections?.[currentTab]?.options || [];
		const newValidationState: UrlValidationState = {};

		currentLinks.forEach((link) => {
			if (link.path && urlRegex.test(link.path)) {
				newValidationState[link.id] = { isValid: true, message: '' };
			} else {
				newValidationState[link.id] = { isValid: false, message: '' };
			}
		});

		setUrlValidationState(newValidationState);
	}, [editModalInputFields, widgetId, currentTab]);

	const handleToggleDisplay = (tab: 'firstTabLinks' | 'thirdTabLinks') => {
		setEditModalInputFields((prevFields) => ({
			...prevFields,
			[widgetId]: {
				...prevFields[widgetId],
				sections: {
					...prevFields[widgetId]?.sections,
					[tab]: {
						...prevFields[widgetId]?.sections?.[tab],
						showSection: !showSection[tab],
					},
				},
			},
		}));

		setShowSection((prev) => ({
			...prev,
			[tab]: !prev[tab],
		}));
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;

		if (name.startsWith('path-')) {
			const linkId = name.split('-')[1];

			setUrlValidationState((prev) => ({
				...prev,
				[linkId]: { isValid: false, message: '' },
			}));

			setEditModalInputFields((prevFields) => ({
				...prevFields,
				[widgetId]: {
					...prevFields[widgetId],
					sections: {
						...prevFields[widgetId]?.sections,
						[currentTab]: {
							...prevFields[widgetId]?.sections?.[currentTab],
							options: prevFields[widgetId]?.sections?.[
								currentTab
							]?.options?.map((link) =>
								link.id === linkId ? { ...link, path: value } : link,
							),
						},
					},
				},
			}));
		} else {
			setEditModalInputFields((prevFields) => ({
				...prevFields,
				[widgetId]: {
					...prevFields[widgetId],
					sections: {
						...prevFields[widgetId]?.sections,
						[currentTab]: {
							...prevFields[widgetId]?.sections?.[currentTab],
							[name]: value,
						},
					},
				},
			}));
		}
	};

	const handleAddLink = () => {
		const updateLinkId = Date.now();
		const newLinkId = `optionalLink${updateLinkId}`;

		const newLink: Link = {
			id: newLinkId,
			label: 'Example Link',
			path: 'https://www.link.com',
			isVisible: true,
		};

		setUrlValidationState((prev) => ({
			...prev,
			[newLinkId]: { isValid: true, message: '' },
		}));

		setEditModalInputFields((prevFields) => ({
			...prevFields,
			[widgetId]: {
				...prevFields[widgetId],
				sections: {
					...prevFields[widgetId]?.sections,
					[currentTab]: {
						...prevFields[widgetId]?.sections?.[currentTab],
						options: [
							...(prevFields[widgetId]?.sections?.[currentTab]?.options || []),
							newLink,
						],
					},
				},
			},
		}));
	};

	const handleDeleteLink = (linkId: string) => {
		setUrlValidationState((prev) => {
			const newState = { ...prev };
			delete newState[linkId];
			return newState;
		});
		setUrlLoadingState((prev) => {
			const newState = { ...prev };
			delete newState[linkId];
			return newState;
		});

		setEditModalInputFields((prevFields) => ({
			...prevFields,
			[widgetId]: {
				...prevFields[widgetId],
				sections: {
					...prevFields[widgetId]?.sections,
					[currentTab]: {
						...prevFields[widgetId]?.sections?.[currentTab],
						options: prevFields[widgetId]?.sections?.[
							currentTab
						]?.options?.filter((link) => link.id !== linkId),
					},
				},
			},
		}));
	};

	const handleEditClick = (link: Link) => {
		setEditingLinkId(link.id);
		setEditLinkName(link.label);
	};

	const handleSaveLabel = (linkId: string) => {
		setEditModalInputFields((prevFields) => ({
			...prevFields,
			[widgetId]: {
				...prevFields[widgetId],
				sections: {
					...prevFields[widgetId]?.sections,
					[currentTab]: {
						...prevFields[widgetId]?.sections?.[currentTab],
						options: prevFields[widgetId]?.sections?.[currentTab]?.options?.map(
							(link) =>
								link.id === linkId ? { ...link, label: editLinkName } : link,
						),
					},
				},
			},
		}));
		setEditingLinkId(null);
	};

	const validateUrl = async (linkId: string, url: string) => {
		if (!url?.trim()?.length) {
			setUrlValidationState((prev) => ({
				...prev,
				[linkId]: { isValid: false, message: t('thisFieldIsRequired') },
			}));
			return;
		}

		if (!urlRegex.test(url.trim())) {
			setUrlValidationState((prev) => ({
				...prev,
				[linkId]: {
					isValid: false,
					message: t('invalidUrlNote') || 'Please enter a valid URL',
				},
			}));
			return;
		}

		setUrlLoadingState((prev) => ({ ...prev, [linkId]: true }));

		try {
			const response: any = await getValidUrl(url.trim(), () => {});
			if (response.STATUS === 0) {
				setUrlValidationState((prev) => ({
					...prev,
					[linkId]: { isValid: true, message: '' },
				}));
				handleInputChange({
					target: { name: `path-${linkId}`, value: response.validURL },
				} as React.ChangeEvent<HTMLInputElement>);
			} else if (response.STATUS === 2) {
				setUrlValidationState((prev) => ({
					...prev,
					[linkId]: {
						isValid: false,
						message: t('invalidUrlNote') || 'Please enter a valid URL',
					},
				}));
			}
		} catch (error) {
			setUrlValidationState((prev) => ({
				...prev,
				[linkId]: {
					isValid: false,
					message: t('invalidUrlNote') || 'Please enter a valid URL',
				},
			}));
		} finally {
			setUrlLoadingState((prev) => ({ ...prev, [linkId]: false }));
		}
	};

	const handleDragStart = (e: React.DragEvent<HTMLDivElement>, linkId: string) => {
		e.dataTransfer.setData('text/plain', linkId);
		(e.target as HTMLDivElement).style.opacity = '0.4';
	};

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetLinkId: string) => {
		e.preventDefault();
		const sourceLinkId = e.dataTransfer.getData('text/plain');
		if (sourceLinkId === targetLinkId) return;

		const currentOptions =
			editModalInputFields[widgetId]?.sections?.[currentTab]?.options || [];
		const oldIndex = currentOptions.findIndex(
			(link) => link.id === sourceLinkId,
		);
		const newIndex = currentOptions.findIndex(
			(link) => link.id === targetLinkId,
		);

		const newOptions = [...currentOptions];
		const [movedItem] = newOptions.splice(oldIndex, 1);
		newOptions.splice(newIndex, 0, movedItem);

		setEditModalInputFields((prev) => ({
			...prev,
			[widgetId]: {
				...prev[widgetId],
				sections: {
					...prev[widgetId]?.sections,
					[currentTab]: {
						...prev[widgetId]?.sections?.[currentTab],
						options: newOptions,
					},
				},
			},
		}));
	};

	const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
		(e.target as HTMLDivElement).style.opacity = '1';
	};

	const renderLinks = (links: Link[] = []) => {
		return links?.map((link) => (
			<div
				key={link.id}
				draggable
				className="d-flex flex-column gap-3 w-100"
				onDragEnd={handleDragEnd}
				onDragOver={handleDragOver}
				onDragStart={(e) => handleDragStart(e, link.id)}
				onDrop={(e) => handleDrop(e, link.id)}
			>
				<div className="d-flex align-items-center gap-3">
					{showSection[currentTab as 'firstTabLinks' | 'thirdTabLinks'] && (
						<BsGripVertical className="text-secondary cursor-grab" size={16} />
					)}
					{editingLinkId === link.id ? (
						<div className="d-flex flex-column w-100">
							<div className="edit-otherlinks-widget-container d-flex align-items-center gap-2 flex-grow-1 pe-2">
								<Form.Control
									className={`edit-otherlinks-widget-container-input ${!editLinkName ? 'is-invalid border-danger' : ''} rounded-start`}
									type="text"
									value={editLinkName}
									onChange={(e) => setEditLinkName(e.target.value)}
								/>
								<div className="d-flex gap-2">
									<div className="save-icon d-flex align-items-center justify-content-center cursor-pointer">
										<BsCheckLg
											className="cursor-pointer"
											color="#FFFFFF"
											size={16}
											onClick={() => handleSaveLabel(link.id)}
										/>
									</div>
									<div className="delete-icon d-flex align-items-center justify-content-center cursor-pointer">
										<BsXLg
											className="cursor-pointer"
											color="#FFFFFF"
											size={16}
											onClick={() => setEditingLinkId(null)}
										/>
									</div>
								</div>
							</div>
							{!editLinkName && (
								<span className="text-danger mt-1">
									{t('thisFieldIsRequired')}
								</span>
							)}
						</div>
					) : (
						<TruncatedDescription
							eventName={link?.label}
							style="link-label"
							type="description"
						/>
					)}
					{editingLinkId !== link.id && showSection[currentTab as 'firstTabLinks' | 'thirdTabLinks'] && (
						<div className="d-flex align-items-center ms-auto gap-2 me-2 cursor-pointer">
							<ActionButton
								variant="edit"
								onClick={() => {
									handleEditClick(link);
								}}
							/>
							<ActionButton
								variant="delete"
								onClick={() => {
									handleDeleteLink(link.id);
								}}
							/>
						</div>
					)}
				</div>
				<Form.Group>
					<Form.Label className="mb-1 d-flex justify-content-between">
						<div>
							URL
							<span className="text-danger">*</span>
						</div>
						{urlValidationState[link.id]?.isValid && link?.path ? (
							<a
								className="text-primary fw-normal"
								href={link.path}
								rel="noopener noreferrer"
								target="_blank"
							>
								{t('testThisLink') || 'Test this link'}
							</a>
						) : (
							<span className="text-muted fw-normal">
								{t('testThisLink') || 'Test this link'}
							</span>
						)}
					</Form.Label>
					<BayFieldWithSpinner
						disabled={!showSection[currentTab as 'firstTabLinks' | 'thirdTabLinks']}
						id={`path-${link.id}`}
						isFieldLoading={urlLoadingState[link.id] || false}
						isInvalid={
							!link?.path || urlValidationState[link.id]?.message?.length > 0
						}
						maxLength={512}
						name={`path-${link.id}`}
						placeholder="Enter..."
						style={{
							backgroundColor: !showSection[currentTab as 'firstTabLinks' | 'thirdTabLinks'] ? '#EDEDEE' : '#FFFFFF',
						}}
						value={link?.path || ''}
						onBlurFunc={(e: React.FocusEvent<HTMLInputElement>) => {
							validateUrl(link.id, e.target.value);
						}}
						onChangeFunc={handleInputChange}
					/>
					{(!link?.path ||
						urlValidationState[link.id]?.message?.length > 0) && (
						<div className="invalid-feedback d-block text-danger mt-1">
							{!link?.path
								? t('thisFieldIsRequired')
								: urlValidationState[link.id]?.message}
						</div>
					)}
				</Form.Group>
			</div>
		));
	};

	return (
		<div className="edit-otherlinks-widget">
			<Tabs
				fill
				unmountOnExit
				defaultActiveKey={currentTab}
				onSelect={(tab) => {
					setCurrentTab(tab as TabKey);
				}}
			>
				<Tab
					className="py-3 px-4"
					eventKey="firstTabLinks"
					title={
						widgetFinishStatus[widgetId]
							? widgetCustomizationData?.sections?.firstTabLinks?.sectionTitle
							: launchpadSettingsData?.widgets?.[widgetId]?.sections
									?.firstTabLinks?.sectionTitle
					}
				>
					<div className="d-flex flex-column gap-4">
						<div>
							<Form.Label>Display</Form.Label>
							<BaySwitch
								checked={showSection['firstTabLinks'] ?? false}
								id="display-section"
								label={
									showSection['firstTabLinks'] ? 'Show Section' : 'Hide Section'
								}
								onChange={() => handleToggleDisplay('firstTabLinks')}
							/>
						</div>
						<Form.Group className="mb-3">
							<div
								className={`position-relative ${!editModalInputFields?.[widgetId]?.sections?.[currentTab]?.sectionTitle || (editModalInputFields?.[widgetId]?.sections?.[currentTab]?.sectionTitle && editModalInputFields?.[widgetId]?.sections?.[currentTab]?.sectionTitle.length > 16) ? 'border-danger' : ''}`}
							>
								<Form.Label>
									Section Title
									<span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									className={`${
										!editModalInputFields?.[widgetId]?.sections?.[currentTab]
											?.sectionTitle ||
										(editModalInputFields?.[widgetId]?.sections?.[currentTab]
											?.sectionTitle &&
											editModalInputFields?.[widgetId]?.sections?.[currentTab]
												?.sectionTitle.length > 16)
											? 'is-invalid'
											: ''
									} pe-7`}
									disabled={!showSection['firstTabLinks']}
									name="sectionTitle"
									placeholder="Enter..."
									style={{
										backgroundColor: !showSection['firstTabLinks']
											? '#EDEDEE'
											: '#FFFFFF',
									}}
									type="text"
									value={
										editModalInputFields?.[widgetId]?.sections?.[currentTab]
											?.sectionTitle || ''
									}
									onChange={handleInputChange}
								/>

								<span
									className={`section-title-length-counter position-absolute end-0 pe-2  fw-bold ${(editModalInputFields?.[widgetId]?.sections?.[currentTab]?.sectionTitle?.length || 0) > 16 ? 'text-danger' : 'text-muted'}`}
								>
									{
										editModalInputFields?.[widgetId]?.sections?.[currentTab]
											?.sectionTitle?.length || 0
									}
									/{16}
								</span>
							</div>
							{editModalInputFields?.[widgetId]?.sections?.[currentTab]
								?.sectionTitle && editModalInputFields?.[widgetId]?.sections?.[currentTab]
								?.sectionTitle.length > 16 && (
								<span className="text-danger mt-1">
									{t('mustBeLessThanNCharacters', '16')}
								</span>
							)}
							{!editModalInputFields?.[widgetId]?.sections?.[currentTab]
								?.sectionTitle && (
								<span className="text-danger fw-normal mt-1">
									{t('thisFieldIsRequired')}
								</span>
							)}
						</Form.Group>
						<div className="w-100 border-top border-secondary-subtle" />
						<span className="fw-semibold fs-5 text-dark">
							{t('availableLinks')}
						</span>
						{renderLinks(
							editModalInputFields?.[widgetId]?.sections?.[currentTab]?.options,
						)}
						{showSection['firstTabLinks'] && (
							<div
								className="d-flex align-items-center gap-2 text-primary cursor-pointer"
								onClick={handleAddLink}
							>
								<BsPlusLg color="#1571F0" size={16} />
								<span className="add-link-text fw-medium text-primary">
									Add Link
								</span>
							</div>
						)}
					</div>
				</Tab>

				<Tab
					className="py-3 px-4"
					eventKey="secondTabLinks"
					title={
						widgetFinishStatus[widgetId]
							? widgetCustomizationData?.sections?.secondTabLinks?.sectionTitle
							: launchpadSettingsData?.widgets?.[widgetId]?.sections
									?.secondTabLinks?.sectionTitle
					}
				>
					<div className="d-flex flex-column gap-4">
						<Form.Group className="mb-3">
							<div
								className={`position-relative ${!editModalInputFields?.[widgetId]?.sections?.[currentTab]?.sectionTitle || editModalInputFields?.[widgetId]?.sections?.[currentTab]?.sectionTitle && editModalInputFields?.[widgetId]?.sections?.[currentTab]?.sectionTitle.length > 16 ? 'border-danger' : ''}`}
							>
								<Form.Label>
									Section Title
									<span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									className={`${
										!editModalInputFields?.[widgetId]?.sections?.[currentTab]
											?.sectionTitle ||
										(editModalInputFields?.[widgetId]?.sections?.[currentTab]
											?.sectionTitle &&
											editModalInputFields?.[widgetId]?.sections?.[currentTab]
												?.sectionTitle.length > 16)
											? 'is-invalid'
											: ''
									} pe-7`}
									name="sectionTitle"
									placeholder="Enter..."
									type="text"
									value={
										editModalInputFields?.[widgetId]?.sections?.[currentTab]
											?.sectionTitle || ''
									}
									onChange={handleInputChange}
								/>

								<span
									className={`section-title-length-counter position-absolute end-0 pe-2  fw-bold ${(editModalInputFields?.[widgetId]?.sections?.[currentTab]?.sectionTitle?.length || 0) > 16 ? 'text-danger' : 'text-muted'}`}
								>
									{
										editModalInputFields?.[widgetId]?.sections?.[currentTab]
											?.sectionTitle?.length || 0
									}
									/{16}
								</span>
							</div>
							{editModalInputFields?.[widgetId]?.sections?.[currentTab]
								?.sectionTitle && editModalInputFields?.[widgetId]?.sections?.[currentTab]
								?.sectionTitle.length > 16 && (
								<div className="text-danger mt-1">
								{t('mustBeLessThanNCharacters', '16')}
								</div>
							)}
							{!editModalInputFields?.[widgetId]?.sections?.[currentTab]
								?.sectionTitle && (
								<div className="text-danger fw-normal mt-1">
									{t('thisFieldIsRequired')}
								</div>
							)}
						</Form.Group>
						<div className="w-100 border-top border-secondary-subtle" />
						<div className="fw-semibold fs-5 text-dark">
							{t('availableLinks')}
						</div>
						{renderLinks(
							editModalInputFields?.[widgetId]?.sections?.[currentTab]?.options,
						)}
					</div>
				</Tab>

				<Tab
					className="py-3 px-4"
					eventKey="thirdTabLinks"
					title={
						widgetFinishStatus[widgetId]
							? widgetCustomizationData?.sections?.thirdTabLinks?.sectionTitle
							: launchpadSettingsData?.widgets?.[widgetId]?.sections
									?.thirdTabLinks?.sectionTitle
					}
				>
					<div className="d-flex flex-column gap-4">
						<div>
							<Form.Label>Display</Form.Label>
							<BaySwitch
								checked={showSection['thirdTabLinks'] ?? false}
								id="display-section"
								label={
									showSection['thirdTabLinks']
										? 'Show Resources Section'
										: 'Hide Resources Section'
								}
								onChange={() => handleToggleDisplay('thirdTabLinks')}
							/>
						</div>
						<Form.Group className="mb-3">
							<div
								className={`position-relative ${!editModalInputFields?.[widgetId]?.sections?.[currentTab]?.sectionTitle || editModalInputFields?.[widgetId]?.sections?.[currentTab]?.sectionTitle && editModalInputFields?.[widgetId]?.sections?.[currentTab]?.sectionTitle.length > 16 ? 'border-danger' : ''}`}
							>
								<Form.Label>
									Section Title
									<span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									className={`${
										!editModalInputFields?.[widgetId]?.sections?.[currentTab]
											?.sectionTitle ||
										(editModalInputFields?.[widgetId]?.sections?.[currentTab]
											?.sectionTitle &&
											editModalInputFields?.[widgetId]?.sections?.[currentTab]
												?.sectionTitle.length > 16)
											? 'is-invalid'
											: ''
									} pe-7`}
									disabled={!showSection['thirdTabLinks']}
									name="sectionTitle"
									placeholder="Enter..."
									style={{
										backgroundColor: !showSection['thirdTabLinks']
											? '#EDEDEE'
											: '#FFFFFF',
									}}
									type="text"
									value={
										editModalInputFields?.[widgetId]?.sections?.[currentTab]
											?.sectionTitle || ''
									}
									onChange={handleInputChange}
								/>

								<span
									className={`section-title-length-counter position-absolute end-0 pe-2  fw-bold ${(editModalInputFields?.[widgetId]?.sections?.[currentTab]?.sectionTitle?.length || 0) > 16 ? 'text-danger' : 'text-muted'}`}
								>
									{
										editModalInputFields?.[widgetId]?.sections?.[currentTab]
											?.sectionTitle?.length || 0
									}
									/{16}
								</span>
							</div>
							{editModalInputFields?.[widgetId]?.sections?.[currentTab]
								?.sectionTitle && editModalInputFields?.[widgetId]?.sections?.[currentTab]
								?.sectionTitle.length > 16 && (
								<div className="text-danger mt-1">
								{t('mustBeLessThanNCharacters', '16')}
								</div>
							)}
							{!editModalInputFields?.[widgetId]?.sections?.[currentTab]
								?.sectionTitle && (
								<div className="text-danger fw-normal mt-1">
									{t('thisFieldIsRequired')}
								</div>
							)}
						</Form.Group>
						<div className="w-100 border-top border-secondary-subtle" />
						<div className="fw-semibold fs-5 text-dark">
							{t('availableLinks')}
						</div>
						{renderLinks(
							editModalInputFields?.[widgetId]?.sections?.[currentTab]?.options,
						)}
					</div>
				</Tab>
			</Tabs>
		</div>
	);
};

export default EditOtherlinks;
